import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  EventEmitter,
  Inject,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as SignInValidators from '../../validators/sign-in.validators';
import { Observable, Subscription } from 'rxjs';
import {
  UpdateSignInStoreErrorAction,
  UserNotLmwRegisteredError,
  UserUnverifiedViaLMWError,
  UserUnverifiedViaOCNError,
} from '../../store/sign-in.actions';
import { signInPasswordState } from '../../routing/route-locales/sign-in-routes';
import { lmwAuthLazyLoadedState } from '../../../common/routing/route-locales/app-routes';
import {
  registrationLazyLoaded, registrationUnverified,
  registrationVerificationCodeContinued,
} from '../../../registration/routing/route-locales/registration-routes';
import {
  parsePhoneNumber,
  isPossiblePhoneNumber,
  CountryCode,
} from 'libphonenumber-js';
import { countryCodeISO } from '../../../providers/country-code';
import { SignInService } from '../../services/sign-in.service';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { ofType, Actions } from '@ngrx/effects';
import { TranslateService } from '@nx/mono-common';
import { AuthProcessProvider } from '../../../providers/auth-process-provider';
import { SignInCms } from '../../interfaces/sign-in-cms.interface';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'auth-process-sign-in-username',
  templateUrl: './sign-in-username.component.html',
})
export class SignInUsernameComponent implements OnInit, OnDestroy {
  @ViewChild('userSearch') userSearchInput: ElementRef;

  public signInUsernameForm: FormGroup;
  public canNavigate = false;
  public referringPageTitle = '';
  public temporarySigningInFromHelpHTML =
    'You need to be signed in to visit';
  public email = '';
  public phoneNumber = '';
  public updateSignInStoreErrorAction: string = UpdateSignInStoreErrorAction;
  public showLoader: Observable<
    boolean
  > = this.signInService.selectSignInPending();
  public linkToRegistration = `/${lmwAuthLazyLoadedState.path}/${registrationLazyLoaded.path}`;
  public removeHelpMessage: EventEmitter<string> = new EventEmitter();
  public _userSearchInput: ElementRef;
  public openNotLmwRegisteredHelp = false;
  public openUserUnverifiedViaLmwHelp = false;
  public openUserUnverifiedViaOCNHelp = false;

  public signInCms: SignInCms;

  private emailOrPhoneNumberEntered: string;
  private signInPasswordStage = `../${signInPasswordState.path}`;
  private formSubscription: Subscription;
  private enteredFromReferrerPage: boolean;

  constructor(
    @Inject(countryCodeISO) private countryCodeISO: CountryCode,
    public authProcessProvider: AuthProcessProvider,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private signInService: SignInService,
    private actions: Actions,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.signInCms = this.route.snapshot.data.signInCmsData;
    this.enteredFromReferrerPage = this.route.snapshot.data.enteredFromReferrerPage
    this.createForm();
    this.subscribeToFormValueChanges();
    this.subscribeToVerifyRegisterCodeError();
    this.checkPreviousAnswer(this.route.snapshot.data.signInDetails);
    this.checkIfRefererPage();
  }

  ngAfterContentChecked() {
    this._userSearchInput = this.userSearchInput;
  }

  public doneButtonClicked() {
    this.emailOrPhoneNumberEntered = this.signInUsernameForm.value.userName.toLowerCase();
    this.setEmailAndPhoneNumber(this.emailOrPhoneNumberEntered);
    this.canNavigate = true;
    this.closeExistingHelpPopovers();
  }

  public translate(template: string) {
    return this.translateService.translate(
      template,
      this.authProcessProvider.language
    );
  }

  public goToNextStage() {
    if (this.phoneNumber !== '') {
      this.emailOrPhoneNumberEntered = this.addCountryCodeToNumber(
        this.emailOrPhoneNumberEntered
      );
    }
    this.canNavigate = false;
    const usernameEmailOrPhoneObject = {
      email: this.email,
      phoneNumber: this.phoneNumber,
    };
    const relativeNavigation = {
      nextState: this.signInPasswordStage,
      previousState: this.route,
    };
    this.signInService.dispatchUpdateSignInStore(
      usernameEmailOrPhoneObject,
      this.emailOrPhoneNumberEntered,
      relativeNavigation
    );
  }

  public onHelpMessageClose() {
    this.focusInputOnUserSearch();
  }

  public onUserSearchFocus() {
    this.closeExistingHelpPopovers();
  }

  public checkPreviousAnswer(signInDetails) {
    if (signInDetails.email) {
      this.signInUsernameForm.patchValue({ ['userName']: signInDetails.email });
      this.doneButtonClicked();
    } else if (signInDetails.phoneNumber) {
      this.phoneNumber = this.removeCountryCodeFromNumber(
        signInDetails.phoneNumber
      );
      this.signInUsernameForm.patchValue({ ['userName']: this.phoneNumber });
      this.doneButtonClicked();
    }
  }

  public toggleNotLmwRegisteredHelp(toggle: boolean) {
    this.openNotLmwRegisteredHelp = toggle;
  }

  public toggleUserUnverifiedViaLmwHelp(toggle: boolean) {
    this.openUserUnverifiedViaLmwHelp = toggle;
  }

  public toggleUserUnverifiedViaOCNHelp(toggle: boolean) {
    this.openUserUnverifiedViaOCNHelp = toggle;
  }

  public getUserUnverifiedViaLMWHtml() {
    const signInValue = encodeURIComponent(this.emailOrPhoneNumberEntered);
    const description = this.email
      ? this.signInCms?.data?.attributes?.username?.lmwRegisterAttemptedHelp
          ?.emailDescription
      : this.signInCms?.data?.attributes?.username?.lmwRegisterAttemptedHelp
          ?.mobileDescription;
    return `<p>${description}</p><p><a href="${this.getContinueWithVerificationUrl()}?signIn=${signInValue}">${this.signInCms?.data?.attributes?.username?.lmwRegisterAttemptedHelp?.verifyRegistrationLinkCopy}</a></p>`;
  }

  private getContinueWithVerificationUrl():string {
    return `${this.linkToRegistration}/${registrationUnverified.path}/${registrationVerificationCodeContinued.path}`
  }

  public closeExistingHelpPopovers() {
    this.removeHelpMessageEmit();
    this.toggleNotLmwRegisteredHelp(false);
    this.toggleUserUnverifiedViaLmwHelp(false);
    this.toggleUserUnverifiedViaOCNHelp(false);
  }

  private createReferringPageTitleFromURL(path) {
    const nameWithoutDashSlashOrQueryParam = path
      .split('?')[0]
      .substring(1)
      .replace(/\-/g, ' ');
    return (
      nameWithoutDashSlashOrQueryParam[0].toUpperCase() +
      nameWithoutDashSlashOrQueryParam.slice(1)
    );
  }

  private removeCountryCodeFromNumber(number: string): string {
    const phoneNumber = parsePhoneNumber(number, this.countryCodeISO);
    return phoneNumber.formatNational();
  }

  private addCountryCodeToNumber(number: string): string {
    const phoneNumber = parsePhoneNumber(number, this.countryCodeISO);
    return phoneNumber.number.toString();
  }

  private isThisAPossiblePhoneNumber(number: string) {
    return isPossiblePhoneNumber(number, this.countryCodeISO);
  }

  private removeHelpMessageEmit() {
    this.removeHelpMessage.next(this.updateSignInStoreErrorAction);
  }

  private focusInputOnUserSearch() {
    this.userSearchInput.nativeElement.focus();
  }

  private createForm() {
    this.signInUsernameForm = this.formBuilder.group({
      userName: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        SignInValidators.emailPhoneNumberValidator(this.countryCodeISO),
      ]),
    });
  }

  private subscribeToFormValueChanges() {
    this.formSubscription = this.signInUsernameForm.valueChanges.subscribe(
      (emailOrPhoneNumber) => {
        if (emailOrPhoneNumber) {
          if (
            this.emailOrPhoneNumberEntered !==
            this.signInUsernameForm.get('userName').value.toLowerCase()
          ) {
            this.canNavigate = false;
          }
        }
      }
    );
  }

  private setEmailAndPhoneNumber(value) {
    if (value.includes('@')) {
      this.email = value;
      this.phoneNumber = '';
    } else if (this.isThisAPossiblePhoneNumber(value)) {
      this.email = '';
      this.phoneNumber = this.addCountryCodeToNumber(value);
    }
  }

  private subscribeToVerifyRegisterCodeError() {
    this.actions
      .pipe(untilDestroyed(this), ofType(UserNotLmwRegisteredError))
      .subscribe(() => {
        this.toggleNotLmwRegisteredHelp(true);
      });
    this.actions
      .pipe(untilDestroyed(this), ofType(UserUnverifiedViaLMWError))
      .subscribe(() => {
        this.toggleUserUnverifiedViaLmwHelp(true);
      });
    this.actions
      .pipe(untilDestroyed(this), ofType(UserUnverifiedViaOCNError))
      .subscribe(() => {
        this.toggleUserUnverifiedViaOCNHelp(true);
      });
  }

  private checkIfRefererPage() {
    if(this.enteredFromReferrerPage) {
      this.signInService.selectSignInStore().pipe(take(1)).subscribe((store) => {
        this.referringPageTitle = this.createReferringPageTitleFromURL(
          store.previousRoute
        );
        this.temporarySigningInFromHelpHTML +=
          " '" + this.referringPageTitle + "'.";
      });
    }
  }

  ngOnDestroy(): void {
    this.formSubscription.unsubscribe();
  }
}
